import { FileButton } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { Button } from '@zorro/design-system/v2/Button';
import { Title } from '@zorro/design-system/v2/Title';
import { Group, Stack, Text } from '@zorro/web/design-system';
import { ReactNode } from 'react';

import { EmptyFileSection } from '../EmptyFileSection';

type Props = {
  /** Optional header row above the list. */
  title?: string;
  /** Muted note beside the title, naming who sees these documents. */
  subtitle?: string;
  /**
   * Whether the viewer may add documents. False removes the upload button and
   * turns the empty state into a read-only "nothing here yet" card.
   */
  canManage: boolean;
  /**
   * The empty state replaces the list. The caller decides what "empty" spans —
   * the admin Files tab counts a fabricated ICHRA card, for instance.
   */
  isEmpty: boolean;
  /** The document cards. */
  children: ReactNode;
  /** Direct upload: the add button picks files and the empty state takes drops. */
  onUpload?: (files: File[]) => void;
  /** Opens a form instead of uploading; wins over `onUpload` for the button. */
  onAddClick?: () => void;
  /**
   * Where empty-state drops land when not straight in `onUpload` — the
   * employer tabs open their metadata form with the dropped file instead.
   */
  onEmptyUpload?: (files: File[]) => void;
  isMultiple?: boolean;
  /** Accessible name for the add button, naming the section it uploads into. */
  uploadLabel?: string;
  readOnlyEmptySubtitle?: string;
};

/**
 * The one shell behind every document list: cards with an "Upload a file"
 * button while there are documents, a drop target while there are none, and
 * nothing to manage by for a read-only viewer. The Files tabs and the
 * employer document pages all render through it, so the affordances cannot
 * drift apart again.
 */
export const DocumentListSection = ({
  title,
  subtitle,
  canManage,
  isEmpty,
  children,
  onUpload,
  onAddClick,
  onEmptyUpload,
  isMultiple = false,
  uploadLabel = 'Upload a file',
  readOnlyEmptySubtitle,
}: Props) => {
  const handleEmptyUpload = onEmptyUpload ?? onUpload;

  const buttonBody = {
    size: 'md',
    variant: 'secondary',
    leftSection: <IconPlus />,
  } as const;

  return (
    <Stack>
      {title && (
        <Group wrap="nowrap" gap="xxs">
          <Title order={4}>{title}</Title>
          {subtitle && (
            <Text c="var(--color-text-default-secondary)">{subtitle}</Text>
          )}
        </Group>
      )}
      {isEmpty ? (
        <EmptyFileSection
          isReadOnly={!canManage}
          subtitleText={`Drag and drop ${isMultiple ? 'files' : 'a file'} here, or click to browse`}
          readOnlySubtitle={
            readOnlyEmptySubtitle ??
            'Enrollment documents will be uploaded here'
          }
          isMultiple={isMultiple}
          onUpload={(files) => handleEmptyUpload?.(files)}
        />
      ) : (
        <>
          <Stack gap="md">{children}</Stack>
          {canManage && (onAddClick ?? onUpload) && (
            <Group>
              {onAddClick ? (
                <Button
                  {...buttonBody}
                  aria-label={uploadLabel}
                  onClick={onAddClick}
                >
                  Upload a file
                </Button>
              ) : (
                <FileButton
                  multiple={isMultiple}
                  onChange={(picked: File[] | File | null) => {
                    if (!picked) {
                      return;
                    }
                    onUpload?.(Array.isArray(picked) ? picked : [picked]);
                  }}
                >
                  {(fileButtonProps) => (
                    <Button
                      aria-label={uploadLabel}
                      {...fileButtonProps}
                      {...buttonBody}
                    >
                      Upload a file
                    </Button>
                  )}
                </FileButton>
              )}
            </Group>
          )}
        </>
      )}
    </Stack>
  );
};
